import React, { useEffect, useState, useCallback } from "react";
import { Users, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";

const fmtUSD = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const TIER_LABEL_KEY = { scout: "tier_scout", refer_close: "tier_refer_close", operator: "tier_operator" };

export default function DownlineTable({ lang }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/scout/downline");
      setData(data);
    } catch {
      setData({ sub_affiliates: [], total_passive_usd: 0, parent_passive_rate_pct: 0 });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return null;

  return (
    <section
      className="eigoo-card p-6 sm:p-7 anim-fade-up anim-delay-3"
      data-testid="downline-section"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <div>
          <h3 className="font-display text-lg sm:text-xl font-bold flex items-center gap-2">
            <Users className="w-5 h-5 text-[#f3c669]" />
            {t(lang, "downline_title")}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            {t(lang, "downline_sub")} · {data.parent_passive_rate_pct}% {t(lang, "passive_rate")}
          </p>
        </div>
        <div className="rounded-xl border border-[#d4a843]/30 bg-[#0a1628]/70 px-4 py-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#f3c669]">
            {t(lang, "downline_total_passive")}
          </div>
          <div
            className="font-display text-2xl font-extrabold gold-gradient-text tabular-nums"
            data-testid="downline-total-passive"
          >
            {fmtUSD(data.total_passive_usd)}
          </div>
        </div>
      </div>

      {(!data.sub_affiliates || data.sub_affiliates.length === 0) ? (
        <div className="border border-dashed border-[#1e3a61] rounded-xl py-10 text-center bg-[#0f2038]/40">
          <p className="text-sm font-semibold text-slate-200">{t(lang, "no_downline")}</p>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            {t(lang, "no_downline_sub")}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="downline-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-[#1e3a61]">
                <th className="py-3 pr-4">{t(lang, "col_scout")}</th>
                <th className="py-3 pr-4">{t(lang, "col_tier")}</th>
                <th className="py-3 pr-4 text-right">{t(lang, "col_signed")}</th>
                <th className="py-3 pr-4 text-right">{t(lang, "col_passive_earnings")}</th>
              </tr>
            </thead>
            <tbody>
              {data.sub_affiliates.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-[#1e3a61]/50 hover:bg-[#1a3254]/40 transition-colors"
                  data-testid={`downline-row-${s.id}`}
                >
                  <td className="py-3 pr-4">
                    <div className="font-semibold text-white">{s.name}</div>
                    <div className="text-[11px] font-mono text-[#f3c669]/80">#{s.referral_code}</div>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#1e3a61] bg-[#0f2038] px-2 py-0.5 text-[10px] uppercase tracking-widest text-slate-300">
                      {t(lang, TIER_LABEL_KEY[s.tier] || "tier_scout")}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-right font-mono tabular-nums text-slate-200">
                    {s.signed_count}
                  </td>
                  <td className="py-3 pr-4 text-right font-mono tabular-nums font-semibold text-[#f3c669]">
                    {fmtUSD(s.passive_earnings_usd)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex items-center gap-1.5 text-[11px] text-slate-500">
        <ArrowRight className="w-3 h-3" /> {t(lang, "downline_hint")}
      </div>
    </section>
  );
}
